import React, { useCallback, useEffect, useState } from 'react';
import integrationsService from '../../services/integrationsService';
import { apiErrorMessage } from '../../utils/apiError';
import '../shared/ConfirmModal.css';

// SCHEDULE RECURRING JOBS for a pipeline implementation (V710) — the modal the "Schedule" button opens next to Resync.
//
// It lists the PREDEFINED jobs the implementation's source system offers (OHIP: "Scheduled Availability"), each with an
// enable toggle, a friendly cadence (every N hours, or daily at HH:MM) and its own params (availability: how far ahead
// to start, and how many days). Saving a job writes its config; the rate(1-minute) tick runs it on the cadence and the
// pipeline Logs show each run grouped under a "Scheduled Job" title. Superadmin-only (the server enforces it too).

const whenLabel = (v) => (v ? String(v).slice(0, 16).replace('T', ' ') : '—');

// A job's cadence as a UI model: mode 'interval' (every N hours) or 'daily' (at HH:MM).
const cadenceOf = (job) => {
    if (job.daily_at) return { mode: 'daily', hours: 24, dailyAt: String(job.daily_at).slice(0, 5) };
    const mins = job.interval_minutes || 360;
    return { mode: 'interval', hours: Math.max(1, Math.round(mins / 60)), dailyAt: '06:00' };
};

// One job row — its own draft + Save, so jobs are configured independently.
function JobCard({ templateId, implementationId, job, onSaved }) {
    const [enabled, setEnabled] = useState(job.enabled);
    const [cad, setCad] = useState(cadenceOf(job));
    const [params, setParams] = useState({ ...(job.defaults || {}), ...(job.params || {}) });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [saved, setSaved] = useState(false);

    const isAvailability = job.jobType === 'AVAILABILITY';

    const save = async () => {
        setSaving(true); setError(null); setSaved(false);
        try {
            const body = { enabled, params };
            if (cad.mode === 'daily') body.daily_at = cad.dailyAt;
            else body.interval_minutes = Math.max(1, cad.hours) * 60;
            const res = await integrationsService.saveScheduleJob(templateId, implementationId, job.jobType, body);
            setSaved(true);
            if (onSaved) onSaved(res);
        } catch (e) {
            setError(apiErrorMessage(e));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '0.9rem 1rem', marginBottom: '0.75rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 600, color: '#111827' }}>
                    <input type="checkbox" checked={enabled} onChange={(e) => { setEnabled(e.target.checked); setSaved(false); }} />
                    {job.label}
                </label>
                <span style={{ marginLeft: 'auto', fontSize: '0.78rem', color: '#6b7280' }}>
                    {job.enabled ? `last run ${whenLabel(job.last_run_at)} · next ${whenLabel(job.next_run_at)}` : 'not scheduled'}
                </span>
            </div>
            {job.description && <p style={{ margin: '0.4rem 0 0.6rem', fontSize: '0.84rem', color: '#6b7280' }}>{job.description}</p>}

            <fieldset disabled={!enabled} style={{ border: 0, padding: 0, margin: 0, opacity: enabled ? 1 : 0.5 }}>
                {/* CADENCE — friendly presets: every N hours, or daily at a time (UTC, 24-hour per STYLE.md). */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                        <input type="radio" name={`cad-${job.jobType}`} checked={cad.mode === 'interval'}
                            onChange={() => { setCad({ ...cad, mode: 'interval' }); setSaved(false); }} />
                        Every
                        <input type="number" min={1} max={168} value={cad.hours} style={{ width: 64 }}
                            onChange={(e) => { setCad({ ...cad, mode: 'interval', hours: parseInt(e.target.value, 10) || 1 }); setSaved(false); }} />
                        hours
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                        <input type="radio" name={`cad-${job.jobType}`} checked={cad.mode === 'daily'}
                            onChange={() => { setCad({ ...cad, mode: 'daily' }); setSaved(false); }} />
                        Daily at
                        <input type="time" value={cad.dailyAt}
                            onChange={(e) => { setCad({ ...cad, mode: 'daily', dailyAt: e.target.value }); setSaved(false); }} />
                        <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>UTC</span>
                    </label>
                </div>

                {/* PARAMS — availability: the forward window to push (legacy STARTDATE/DAYS). */}
                {isAvailability && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem' }}>
                            Start in
                            <input type="number" min={0} max={365} value={params.start_offset_days ?? 0} style={{ width: 64 }}
                                onChange={(e) => { setParams({ ...params, start_offset_days: parseInt(e.target.value, 10) || 0 }); setSaved(false); }} />
                            days
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem' }}>
                            for
                            <input type="number" min={1} max={365} value={params.days ?? 7} style={{ width: 64 }}
                                onChange={(e) => { setParams({ ...params, days: parseInt(e.target.value, 10) || 1 }); setSaved(false); }} />
                            days ahead
                        </label>
                    </div>
                )}
            </fieldset>

            {error && <div className="cm-message" style={{ marginTop: '0.5rem', color: '#dc2626' }}>{error}</div>}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '0.6rem' }}>
                <button type="button" className="cm-btn-confirm" onClick={save} disabled={saving}>
                    {saving ? 'Saving…' : 'Save'}
                </button>
                {saved && <span style={{ color: '#047857', fontSize: '0.85rem' }}>Saved</span>}
            </div>
        </div>
    );
}

function ScheduleModal({ templateId, implementationId, sourceName, onClose }) {
    const [jobs, setJobs] = useState(null);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        setError(null);
        try {
            const data = await integrationsService.getSchedule(templateId, implementationId);
            setJobs(data.jobs || []);
        } catch (e) {
            setError(apiErrorMessage(e));
        }
    }, [templateId, implementationId]);

    useEffect(() => { load(); }, [load]);

    return (
        <div className="cm-backdrop" onClick={onClose}>
            <div className="cm-dialog cm-dialog-wide" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
                <h2 className="cm-title">Schedule{sourceName ? ` — ${sourceName}` : ''}</h2>
                <p className="cm-message">
                    Recurring jobs that push {sourceName || 'source'} records through this pipeline on a cadence. Each run
                    appears on the pipeline Logs under a “Scheduled Job”.
                </p>
                {error && <div className="cm-message" style={{ color: '#dc2626' }}>{error}</div>}
                {!jobs && !error && <p className="cm-message">Loading…</p>}
                {jobs && jobs.length === 0 && <p className="cm-message">This source has no scheduled jobs.</p>}
                {jobs && jobs.map((job) => (
                    <JobCard
                        key={job.jobType}
                        templateId={templateId}
                        implementationId={implementationId}
                        job={job}
                        onSaved={(res) => { if (res && res.jobs) setJobs(res.jobs); }}
                    />
                ))}
                <div className="cm-actions">
                    <button type="button" className="cm-btn-cancel" onClick={onClose}>Close</button>
                </div>
            </div>
        </div>
    );
}

export default ScheduleModal;
